/*
  Dhrohar product-card colour swatches.
  <dhrohar-card-swatches> (snippets/dhrohar-card-swatches.liquid) holds:
    - <button data-variant-id ...> for values of the product's own Colour option: clicking swaps
      the card image, the card links (?variant=<id>) and the wishlist heart's variant in place.
    - <a href> dots for dhrohar.colour_siblings: ordinary links to the other product.
  Safe to load more than once and to re-run after a section reload or facet update.
*/
(() => {
  if (!window.customElements || customElements.get('dhrohar-card-swatches')) return;

  class DhroharCardSwatches extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-variant-id]');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        try {
          this.select(button);
        } catch (error) {
          console.error(error);
        }
      });
    }

    get card() {
      return this.closest('.card-wrapper') || this.closest('li') || this.parentElement;
    }

    select(button) {
      const card = this.card;
      if (!card) return;
      const buttons = Array.from(this.querySelectorAll('button[data-variant-id]'));
      if (!this._initial) this._initial = buttons.find((b) => b.getAttribute('aria-pressed') === 'true') || buttons[0];
      const isInitial = button === this._initial;
      buttons.forEach((b) => b.setAttribute('aria-pressed', String(b === button)));

      // Image: swap the primary image; the hover image is hidden while another colour is shown.
      const primary = card.querySelector('.card__media img');
      if (primary) {
        if (primary.dataset.dhroharSrc === undefined) {
          primary.dataset.dhroharSrc = primary.getAttribute('src') || '';
          primary.dataset.dhroharSrcset = primary.getAttribute('srcset') || '';
          primary.dataset.dhroharAlt = primary.getAttribute('alt') || '';
        }
        const src = isInitial ? primary.dataset.dhroharSrc : button.dataset.src;
        const srcset = isInitial ? primary.dataset.dhroharSrcset : button.dataset.srcset;
        if (src) {
          primary.removeAttribute('loading');
          primary.setAttribute('srcset', (srcset || '').trim() || src);
          primary.setAttribute('src', src);
          primary.setAttribute('alt', isInitial ? primary.dataset.dhroharAlt : button.dataset.alt || '');
        }
      }
      card.classList.toggle('dhrohar-card--swapped', !isInitial);
      card.classList.add('dhrohar-card--picked');

      // Price: the chosen variant's exact price, sale and sold-out state (pre-formatted in Liquid).
      this.updatePrice(card, button);

      // Links: point every link to this product at the chosen variant.
      const url = button.dataset.url;
      if (url) {
        const base = url.split('?')[0];
        card.querySelectorAll('a[href]').forEach((a) => {
          if (a.closest('dhrohar-card-swatches')) return;
          const href = a.getAttribute('href') || '';
          if (href.split('?')[0] === base) a.setAttribute('href', url);
        });
      }

      // Wishlist heart follows the shown colour.
      const heart = card.querySelector('dhrohar-wishlist-button');
      if (heart) {
        heart.dataset.variantId = button.dataset.variantId;
        heart.dataset.variantTitle = button.dataset.variantTitle || '';
        if (button.dataset.image) heart.dataset.productImage = button.dataset.image;
        if (typeof heart.refresh === 'function') heart.refresh();
      }
    }
  }

  DhroharCardSwatches.prototype.updatePrice = function updatePrice(card, button) {
    const price = card.querySelector('.card-information > .price, .card__information .price');
    if (!price) return;
    if (price.dataset.dhroharHtml === undefined) {
      price.dataset.dhroharHtml = price.innerHTML;
      price.dataset.dhroharClass = price.className;
    }
    // Every explicit selection (including the initial colour) shows that variant's own price;
    // only the untouched card keeps Dawn's product-level price / "From" range.
    if (!button.dataset.price) {
      price.innerHTML = price.dataset.dhroharHtml;
      price.className = price.dataset.dhroharClass;
      return;
    }
    const make = (tag, className, text) => {
      const el = document.createElement(tag);
      if (className) el.className = className;
      el.textContent = text;
      return el;
    };
    const container = make('div', 'price__container dhrohar-price', '');
    const compare = button.dataset.comparePrice;
    if (compare) {
      // 0.6.0: selling price, then "MRP" struck through and the exact saving (same as snippets/price.liquid).
      const mrp = make('span', 'dh-mrp', '');
      mrp.append(make('span', 'dh-mrp__label', 'MRP'), make('span', 'visually-hidden', 'Regular price'), make('s', 'price-item price-item--regular', compare));
      container.append(make('span', 'visually-hidden', 'Sale price'), make('span', 'price-item price-item--sale price-item--last', button.dataset.price), ' ', mrp);
      if (button.dataset.discountPct) container.append(' ', make('span', 'dh-mrp__off', `(${button.dataset.discountPct}% off)`));
    } else {
      container.append(
        make('span', 'visually-hidden', 'Regular price'),
        make('span', 'price-item price-item--regular', button.dataset.price)
      );
    }
    if (button.dataset.available === 'false') container.append(' ', make('span', 'dhrohar-price__soldout', 'Sold out'));
    price.className = 'price dhrohar-price-swapped' + (compare ? ' dhrohar-price-swapped--sale' : '');
    price.replaceChildren(container);
  };

  customElements.define('dhrohar-card-swatches', DhroharCardSwatches);
})();
